/**
 * 더 묻기 첨부 — 파일을 질문 문맥으로 바꾼다(2026-09-14 사용자 요청 "첨부할 수 있도록").
 * 텍스트류(txt·csv·md·json·xml·html)와 PDF는 글을 뽑아 `text`로, 이미지(png·jpg·webp·gif)는 base64 `data`로 보낸다.
 * 서버가 문맥에 붙이고 모델에 이미지 파트로 전달한다. 첨부는 그 질문 한 번에만 붙는다(대화 기록에는 파일명만 남는다).
 * 상한: 파일 4개 · 각 4MB · 텍스트 발췌 8,000자 · PDF 앞 12쪽. 넘거나 못 읽으면 그 파일만 `error`로 표시한다.
 */
export const ATTACH_MAX_FILES = 4;
export const ATTACH_MAX_BYTES = 4 * 1024 * 1024;
export const ATTACH_TEXT_CHARS = 8000;
export const ATTACH_PDF_PAGES = 12;
export const ATTACH_ACCEPT = ".pdf,.txt,.csv,.md,.json,.xml,.html,.png,.jpg,.jpeg,.webp,.gif";

const TEXT_TYPES = /^(text\/|application\/(json|xml|csv))/i;
const IMAGE_TYPES = /^image\/(png|jpe?g|webp|gif)$/i;

export function attachmentKind(file) {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "");
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (IMAGE_TYPES.test(type)) return "image";
  if (TEXT_TYPES.test(type) || /\.(txt|csv|md|json|xml|html?)$/.test(name)) return "text";
  return "unsupported";
}

function readAsText(file) {
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || "")); r.onerror = () => reject(r.error || new Error("read failed")); r.readAsText(file); });
}
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || "")); r.onerror = () => reject(r.error || new Error("read failed")); r.readAsDataURL(file); });
}
function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(r.error || new Error("read failed")); r.readAsArrayBuffer(file); });
}

/** PDF 텍스트 발췌 — pdfjs 는 무거워 필요할 때만 불러온다(App 이 이미 워커를 설정한다). */
async function extractPdfText(file, loadPdfjs) {
  const pdfjs = await (loadPdfjs ? loadPdfjs() : import("pdfjs-dist"));
  if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version || "3.11.174"}/build/pdf.worker.min.js`;
  const data = await readAsArrayBuffer(file);
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pages = Math.min(pdf.numPages, ATTACH_PDF_PAGES);
  let out = "";
  for (let i = 1; i <= pages && out.length < ATTACH_TEXT_CHARS; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    out += `${content.items.map((it) => it.str).join(" ")}\n`;
  }
  return { text: out.trim(), pages: pdf.numPages, extractedPages: pages };
}

/**
 * 파일 하나 → 첨부 항목 `{ id, name, size, kind, mime, text?, data?, note, error? }`.
 * 던지지 않는다 — 실패는 `error`로 돌아온다.
 */
export async function prepareAttachment(file, { loadPdfjs } = {}) {
  const base = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: String(file?.name || "파일"), size: Number(file?.size) || 0, mime: String(file?.type || ""), kind: attachmentKind(file) };
  if (base.size > ATTACH_MAX_BYTES) return { ...base, error: "4MB를 넘습니다" };
  try {
    if (base.kind === "text") {
      const text = (await readAsText(file)).slice(0, ATTACH_TEXT_CHARS);
      return { ...base, text, note: `텍스트 ${text.length.toLocaleString("ko-KR")}자` };
    }
    if (base.kind === "pdf") {
      const { text, pages, extractedPages } = await extractPdfText(file, loadPdfjs);
      if (!text) return { ...base, error: "글자를 뽑을 수 없는 PDF(스캔본)입니다 — 이미지로 저장해 올려 주세요" };
      return { ...base, text: text.slice(0, ATTACH_TEXT_CHARS), note: `PDF ${pages}쪽 중 ${extractedPages}쪽 발췌` };
    }
    if (base.kind === "image") {
      const url = await readAsDataUrl(file);
      const comma = url.indexOf(",");
      return { ...base, data: comma >= 0 ? url.slice(comma + 1) : "", mime: base.mime || "image/png", note: "이미지 · 모델이 직접 봅니다" };
    }
    return { ...base, error: "지원하지 않는 형식입니다(PDF·텍스트·이미지)" };
  } catch (e) {
    return { ...base, error: `읽지 못했습니다: ${String(e?.message || e).slice(0, 80)}` };
  }
}

/** 서버로 보낼 모양 — 오류 항목은 뺀다. */
export function attachmentsPayload(items) {
  return (items || []).filter((a) => a && !a.error).map((a) => ({ name: a.name, mime: a.mime, kind: a.kind, ...(a.text ? { text: a.text } : {}), ...(a.data ? { data_base64: a.data } : {}) }));
}
